import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import SeoHead from '@/components/base/SeoHead';
import { faqItems } from '@/mocks/faq';

const companyStats = [
  { value: '1500+', label: '天稳定运行' },
  { value: '1,000,000+', label: '累计服务用户' },
  { value: '98.5%', label: '用户好评率' },
  { value: '7×24', label: '全天候客服支持' },
];

const reputationItems = [
  {
    name: '张先生',
    role: '资深用户 - 使用3年',
    content: '8866h.cc是我用过最稳定的平台，三年来从未出现过重大问题。客服响应速度非常快，每次遇到问题都能及时解决。强烈推荐给身边的朋友！',
    rating: 5,
  },
  {
    name: '李女士',
    role: '企业用户 - 使用2年',
    content: '作为企业用户，我们对平台的稳定性和安全性要求很高。8866h.cc完全满足我们的需求，数据安全保障做得非常到位，用起来特别放心。',
    rating: 5,
  },
  {
    name: '王同学',
    role: '新用户 - 使用6个月',
    content: '刚接触8866h.cc时被简洁的界面吸引了，上手非常快。新手教程很详细，跟着指南一步步操作，很快就熟悉了全部功能。体验感满分！',
    rating: 5,
  },
  {
    name: '刘总',
    role: '合作伙伴 - 使用1年',
    content: '与8866h.cc合作一年多，团队的专业性和服务意识令人印象深刻。平台持续迭代更新，每次升级都能带来更好的体验。值得信赖的合作伙伴。',
    rating: 5,
  },
  {
    name: '陈小姐',
    role: '活跃用户 - 使用1.5年',
    content: '平台的响应速度真的很快，无论什么时间段访问都很流畅。多端同步功能太方便了，在电脑上没做完的事情，手机上可以继续。大爱！',
    rating: 5,
  },
  {
    name: '赵先生',
    role: '社区达人 - 使用2年',
    content: '8866h.cc的社区氛围特别好，用户之间互帮互助。平台工作人员也会积极参与讨论，倾听用户反馈。在这里找到了归属感。',
    rating: 5,
  },
];

export default function AboutPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website_alt: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const form = e.currentTarget as HTMLFormElement;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '', website_alt: '' });
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const data = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9pid99n2trhb35gvg5g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '', website_alt: '' });
      } else {
        const serverMsg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          responseText ||
          '提交失败，请稍后重试';
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
          setFormStatus('error');
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const aboutSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, item: { '@id': 'https://8866h.cc', name: '首页' } },
        { '@type': 'ListItem', position: 2, item: { '@id': 'https://8866h.cc/about', name: '关于我们' } },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '8866h.cc',
      url: 'https://8866h.cc',
      description: '8866h.cc是您信赖的官方平台入口，致力于为用户提供安全、稳定、高效的在线服务体验。',
      email: 'support@8866h.cc',
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'support@8866h.cc',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.slice(0, 4).map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    },
  ];

  return (
    <>
      <SeoHead
        title="关于我们 - 8866h.cc品牌介绍与联系方式 | 8866h官网"
        description="了解8866h.cc品牌故事、平台使命与核心价值。查看用户口碑评价，获取官方联系方式。8866h.cc，您信赖的官方平台入口。"
        keywords="8866h.cc关于我们,8866h品牌介绍,8866h联系方式,8866h用户口碑,8866h平台介绍"
        canonicalUrl="https://8866h.cc/about"
        schemaData={aboutSchema}
      />

      <Navbar />

      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
            <div className="pb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">关于我们</h1>
            </div>
          </div>
        </div>

        {/* Brand Introduction */}
        <section id="intro" className="py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6 font-heading">
                  关于 8866h.cc
                </h2>
                <div className="space-y-4 text-foreground-600 leading-relaxed">
                  <p>
                    <strong className="text-foreground-900">8866h.cc</strong> 成立于2022年，是一个致力于为用户提供安全、稳定、高效在线服务体验的综合性平台。自成立以来，我们始终坚持「<strong className="text-foreground-900">用户至上、安全第一</strong>」的核心理念，持续投入技术研发和服务优化。
                  </p>
                  <p>
                    平台采用业界领先的技术架构，全球多节点部署，确保用户无论身在何处都能享受毫秒级的响应速度。同时，我们建立了完善的安全防护体系，全方位保障用户数据和隐私安全。
                  </p>
                  <p>
                    经过多年的深耕发展，<strong className="text-foreground-900">8866h.cc</strong> 已累计服务超过百万用户，赢得了广泛的好评与信赖。我们的客服团队7×24小时在线，随时为用户提供专业的技术支持和服务。
                  </p>
                  <p>
                    展望未来，<strong className="text-foreground-900">8866h.cc</strong> 将继续秉持初心，不断突破创新，为用户创造更大的价值。我们相信，在全体用户的信任与支持下，8866h.cc必将迎来更加辉煌的明天。
                  </p>
                </div>
              </div>
              <div className="flex-1">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20workspace%20with%20warm%20golden%20accents%2C%20abstract%20geometric%20art%20on%20wall%2C%20soft%20natural%20light%2C%20cream%20and%20warm%20neutral%20tones%2C%20professional%20elegant%20atmosphere%2C%20clean%20architectural%20design%2C%20high%20detail%20editorial%20photography&width=600&height=450&seq=about-brand&orientation=landscape"
                  alt="8866h.cc 品牌形象"
                  title="8866h.cc - 官方平台"
                  className="w-full rounded-xl object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {companyStats.map((stat, index) => (
                <div key={index} className="text-center p-6">
                  <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-2 font-heading">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section id="reputation" className="py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 font-heading">
                用户口碑
              </h2>
              <p className="text-foreground-500 max-w-2xl mx-auto">
                来自真实用户的声音，是我们不断前进的动力
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reputationItems.map((item, index) => (
                <div
                  key={index}
                  className="p-6 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: item.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                    &ldquo;{item.content}&rdquo;
                  </p>
                  <div>
                    <div className="text-sm font-semibold text-foreground-950">{item.name}</div>
                    <div className="text-xs text-foreground-400">{item.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 font-heading">
                联系我们
              </h2>
              <p className="text-foreground-500 max-w-2xl mx-auto">
                如有任何问题或建议，欢迎通过以下方式与我们取得联系
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-12">
              <div className="flex-1 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground-950 mb-1">电子邮箱</h4>
                    <p className="text-sm text-foreground-500">support@8866h.cc</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-2-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground-950 mb-1">在线客服</h4>
                    <p className="text-sm text-foreground-500">7×24小时全天候在线，随时为您服务</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-global-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground-950 mb-1">官方域名</h4>
                    <p className="text-sm text-foreground-500">8866h.cc（唯一官方入口，谨防仿冒）</p>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4 p-6 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="您的姓名"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="您的邮箱"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="咨询主题"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="请输入您的留言内容（最多500字符）"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                  </div>
                  <input
                    type="text"
                    name="website_alt"
                    value={formData.website_alt}
                    onChange={(e) => setFormData({ ...formData, website_alt: e.target.value })}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="opacity-0 absolute top-0 left-0 h-0 w-0 -z-10"
                  />
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center">
                      留言已成功发送，我们会尽快回复您！
                    </p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 font-heading">
                常见问题
              </h2>
              <p className="text-foreground-500">快速了解8866h.cc</p>
            </div>

            <div className="space-y-4">
              {faqItems.slice(0, 4).map((item, index) => (
                <details key={index} className="group rounded-xl border border-background-200/70 bg-background-50 overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-100 transition-colors list-none">
                    <span className="text-sm font-medium text-foreground-950 pr-4">{item.question}</span>
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-background-100 flex items-center justify-center group-open:rotate-45 transition-transform">
                      <i className="ri-add-line text-foreground-500 text-sm"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}